import { BaseService } from '../../../../../core/services/base.service';
import { getApiBaseUrl } from '../../../../../core/config/runtimeConfig';
import {
  ClarificationQuestionSource,
  ClarificationRequest,
  DashboardClarificationRequest,
  PublicClarification,
} from '../models/clarification.model';

export class ClarificationService {
  static recent(size = 5): Promise<DashboardClarificationRequest[]> {
    return BaseService.Get(`/clarifications/recent?size=${size}`);
  }

  static list(applicationId: string): Promise<ClarificationRequest[]> {
    return BaseService.Get(`/job-applications/${applicationId}/clarifications`);
  }

  static create(
    applicationId: string,
    questions: Array<{ question: string; source: ClarificationQuestionSource }>,
    channels: { email: boolean; whatsapp: boolean }
  ): Promise<ClarificationRequest> {
    return BaseService.Post(
      `/job-applications/${applicationId}/clarifications`,
      {
        questions,
        sendNow: true,
        ...channels,
      }
    );
  }

  static close(
    applicationId: string,
    requestId: string
  ): Promise<ClarificationRequest> {
    return BaseService.Post(
      `/job-applications/${applicationId}/clarifications/${requestId}/close`,
      {}
    );
  }

  static cancel(
    applicationId: string,
    requestId: string
  ): Promise<ClarificationRequest> {
    return BaseService.Post(
      `/job-applications/${applicationId}/clarifications/${requestId}/cancel`,
      {}
    );
  }

  static review(
    applicationId: string,
    requestId: string,
    questionId: string,
    status: 'SUFFICIENT' | 'FOLLOW_UP_NEEDED'
  ): Promise<ClarificationRequest> {
    return BaseService.Patch(
      `/job-applications/${applicationId}/clarifications/${requestId}/questions/${questionId}`,
      { status }
    );
  }

  private static async publicRequest<T>(
    path: string,
    body?: object
  ): Promise<T> {
    const response = await fetch(`${getApiBaseUrl()}${path}`, {
      method: body ? 'POST' : 'GET',
      headers: {
        accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: body ? JSON.stringify(body) : undefined,
    });
    if (response.status === 410) throw new Error('GONE');
    if (!response.ok) {
      const payload = await response.json().catch(() => ({}));
      throw new Error(payload.message || 'REQUEST_FAILED');
    }
    if (response.status === 204) return undefined as T;
    const text = await response.text();
    return (text ? JSON.parse(text) : undefined) as T;
  }

  static publicView(token: string): Promise<PublicClarification> {
    return this.publicRequest(`/public/clarifications/${token}`);
  }

  static submit(
    token: string,
    answers: Array<{ questionId: string; answer: string }>
  ): Promise<void> {
    return this.publicRequest(`/public/clarifications/${token}/answers`, {
      answers,
    });
  }
}
